import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../../components/AppIcon';
import { apiService } from '../../../../services/api';
import { format } from 'date-fns';

const MDR_RATE = 0.015;

const STEPS = {
  DATE_RANGE: 1,
  BUSINESS_MERCHANT: 2,
  CURRENCY: 3,
  SUMMARY: 4,
  CONFIRM: 5,
  SUCCESS: 6,
};

const STEP_LABELS = ['Date Range', 'Business & Merchants', 'Currency', 'Summary', 'Confirm'];

const formatCurrency = (amount, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 })?.format(amount || 0);

// Dummy businesses with merchants for fallback
const DUMMY_BUSINESSES = [
  {
    id: 'BIZ001',
    name: 'Acme Retail Group',
    merchants: [
      { id: 'MRC001', name: 'Acme Store - Downtown' },
      { id: 'MRC002', name: 'Acme Store - Uptown' },
      { id: 'MRC003', name: 'Acme Online Shop' },
    ],
  },
  {
    id: 'BIZ002',
    name: 'Global Payments Ltd',
    merchants: [
      { id: 'MRC004', name: 'GP - East Branch' },
      { id: 'MRC005', name: 'GP - West Branch' },
    ],
  },
  {
    id: 'BIZ003',
    name: 'TechMart Holdings',
    merchants: [
      { id: 'MRC006', name: 'TechMart Express' },
      { id: 'MRC007', name: 'TechMart Pro' },
      { id: 'MRC008', name: 'TechMart Wholesale' },
    ],
  },
];

// Dummy unsettled transactions generator
const generateDummyTransactions = (merchantIds, startDate, endDate) => {
  let deposits = [];
  let payouts = [];
  let refunds = [];
  merchantIds?.forEach((mid, idx) => {
    const count = 3 + idx;
    for (let i = 0; i < count; i++) {
      deposits?.push({
        id: `TXN-D-${mid}-${i}`,
        merchant_id: mid,
        amount: (500 + Math.random() * 2000)?.toFixed(2),
        currency: 'USD',
        settlement_status: 'UNSETTLED',
        payment_type: 'deposit',
        created_at: startDate,
      });
    }
    payouts?.push({
      id: `TXN-P-${mid}-0`,
      merchant_id: mid,
      amount: (100 + Math.random() * 300)?.toFixed(2),
      currency: 'USD',
      payment_type: 'payout',
    });
    if (idx % 2 === 0) {
      refunds?.push({
        id: `TXN-R-${mid}-0`,
        merchant_id: mid,
        amount: (50 + Math.random() * 150)?.toFixed(2),
        currency: 'USD',
        payment_type: 'refund',
      });
    }
  });
  return { deposits, payouts, refunds };
};

const CreateSettlementModal = ({ onClose, onSettlementCreated }) => {
  const [step, setStep] = useState(STEPS?.DATE_RANGE);

  // Step 1
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Step 2
  const [businesses, setBusinesses] = useState([]);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);

  // Searchable dropdown state
  const [bizSearch, setBizSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Step 3 - Currency
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = useState([]);

  // Step 4
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryDetails, setSummaryDetails] = useState([]); // array of per-currency detail objects
  const [rawTransactions, setRawTransactions] = useState([]);

  // Step 5
  const [confirming, setConfirming] = useState(false);
  const [settlementFee, setSettlementFee] = useState(100);

  const [error, setError] = useState('');

  // Aggregate totals across all currencies (for confirm step display)
  const firstDetail = summaryDetails?.[0];
  const summaryCurrency = firstDetail?.currency || 'USD';

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch businesses when entering step 2
  useEffect(() => {
    if (step === STEPS?.BUSINESS_MERCHANT && businesses?.length === 0) {
      fetchBusinesses();
    }
  }, [step]);

  // Fetch currencies when entering step 3
  useEffect(() => {
    if (step === STEPS?.CURRENCY && currencies?.length === 0) {
      fetchCurrencies();
    }
  }, [step]);

  const fetchBusinesses = async () => {
    setBusinessLoading(true);
    setError('');
    try {
      const res = await apiService?.listBusinesses({ size: 100, page: 0 });
      const records = res?.data?.details || [];
      if (Array.isArray(records) && records?.length > 0) {
        // Normalize from API response: business_code → id, business_name → name, merchants[].merchant_id → id/name
        const normalized = records
          ?.filter(b => Array.isArray(b?.merchants) && b?.merchants?.length > 0)
          ?.map(b => ({
            id: b?.business_code,
            name: b?.business_name,
            merchants: b?.merchants?.map(m => ({
              id: m?.merchant_id,
              name: m?.merchant_id,
            })),
          }));
        setBusinesses(normalized?.length > 0 ? normalized : []);
      } else {
        setBusinesses([]);
      }
    } catch {
      setBusinesses([]);
    } finally {
      setBusinessLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    setCurrencyLoading(true);
    setError('');
    try {
      const res = await apiService?.getCurrencies();
      const records = res?.data?.details || [];
      setCurrencies(Array.isArray(records) ? records : []);
    } catch {
      setCurrencies([]);
    } finally {
      setCurrencyLoading(false);
    }
  };

  const handleSelectBusiness = (biz) => {
    setSelectedBusiness(biz);
    setSelectedMerchantIds([]);
    setBizSearch(biz?.name || '');
    setDropdownOpen(false);
  };

  const toggleMerchant = (mid) => {
    setSelectedMerchantIds(prev =>
      prev?.includes(mid) ? prev?.filter(id => id !== mid) : [...prev, mid]
    );
  };

  const selectAllMerchants = () => {
    const allIds = selectedBusiness?.merchants?.map(m => m?.id) || [];
    setSelectedMerchantIds(allIds);
  };

  const clearAllMerchants = () => setSelectedMerchantIds([]);

  const toggleCurrency = (code) => {
    setSelectedCurrencies(prev =>
      prev?.includes(code) ? prev?.filter(c => c !== code) : [...prev, code]
    );
  };

  const selectAllCurrencies = () => {
    setSelectedCurrencies(currencies?.map(c => c?.currency_code));
  };

  const clearAllCurrencies = () => setSelectedCurrencies([]);

  const fetchSummary = async () => {
    if (!selectedBusiness || selectedMerchantIds?.length === 0) {
      setError('Please select a business and at least one merchant.');
      return;
    }
    if (selectedCurrencies?.length === 0) {
      setError('Please select at least one currency.');
      return;
    }
    setSummaryLoading(true);
    setError('');
    try {
      const res = await apiService?.getSettlementSummary({
        business_code: selectedBusiness?.id,
        merchants: selectedMerchantIds,
        currencies: selectedCurrencies,
        start_date: startDate,
        end_date: endDate,
      });

      const details = res?.data?.details;
      if (Array.isArray(details) && details?.length > 0) {
        const settlementData = details?.[0]?.settlements;
        if (Array.isArray(settlementData) && settlementData?.length > 0) {
          setSummaryDetails(settlementData);
        } else {
          throw new Error('No settlement data returned');
        }
        setRawTransactions([]);
      } else {
        throw new Error('No summary data returned');
      }

      setStep(STEPS?.SUMMARY);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch settlement summary. Please try again.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    try {
      await new Promise(r => setTimeout(r, 800));
      setStep(STEPS?.SUCCESS);
      onSettlementCreated?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create settlement.');
    } finally {
      setConfirming(false);
    }
  };

  const goBack = () => {
    setError('');
    if (step === STEPS?.BUSINESS_MERCHANT) setStep(STEPS?.DATE_RANGE);
    else if (step === STEPS?.CURRENCY) setStep(STEPS?.BUSINESS_MERCHANT);
    else if (step === STEPS?.SUMMARY) setStep(STEPS?.CURRENCY);
    else if (step === STEPS?.CONFIRM) setStep(STEPS?.SUMMARY);
  };

  const isStepDone = (num) => step > num && step !== STEPS?.SUCCESS;
  const isStepActive = (num) => step === num;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] min-h-[520px] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Banknote" size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">Create Settlement</h2>
              <p className="text-xs text-text-secondary">
                {step === STEPS?.DATE_RANGE && 'Step 1 — Select date range'}
                {step === STEPS?.BUSINESS_MERCHANT && 'Step 2 — Select business & merchants'}
                {step === STEPS?.CURRENCY && 'Step 3 — Select currencies'}
                {step === STEPS?.SUMMARY && 'Step 4 — Review unsettled summary'}
                {step === STEPS?.CONFIRM && 'Step 5 — Confirm settlement'}
                {step === STEPS?.SUCCESS && 'Settlement created successfully'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-text-secondary hover:text-text-primary"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Step indicator */}
        {step !== STEPS?.SUCCESS && (
          <div className="px-6 py-3 border-b border-border bg-muted/20 shrink-0">
            <div className="flex items-center space-x-2">
              {STEP_LABELS?.map((label, i) => {
                const num = i + 1;
                const done = isStepDone(num);
                const active = isStepActive(num);
                return (
                  <React.Fragment key={num}>
                    <div className="flex items-center space-x-1.5 min-w-0">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
                          done
                            ? 'bg-success text-white'
                            : active
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-text-secondary'
                        }`}
                      >
                        {done ? <Icon name="Check" size={12} /> : num}
                      </div>
                      <span
                        className={`text-xs font-medium hidden sm:block truncate ${
                          active ? 'text-text-primary' : 'text-text-secondary'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < STEP_LABELS?.length - 1 && (
                      <div className={`flex-1 h-px ${done ? 'bg-success/40' : 'bg-border'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── STEP 1: Date Range ── */}
          {step === STEPS?.DATE_RANGE && (
            <div className="space-y-5">
              <p className="text-sm text-text-secondary">
                Select the settlement period. Unsettled transactions within this date range will be fetched for the merchants you choose in the next step.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Start Date <span className="text-error">*</span>
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e?.target?.value)}
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    End Date <span className="text-error">*</span>
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e?.target?.value)}
                    min={startDate}
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="Info" size={16} className="text-primary mt-0.5 shrink-0" />
                <div className="text-xs text-text-secondary space-y-1">
                  <p>Net Settlement = Deposits − Payouts − Refunds − MDR ({(MDR_RATE * 100)?.toFixed(1)}%)</p>
                  <p>Settlements are per business. You will select the business and its merchants in the next step.</p>
                </div>
              </div>
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} className="text-error shrink-0" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Business & Merchant Selection ── */}
          {step === STEPS?.BUSINESS_MERCHANT && (
            <div className="space-y-5 min-h-[340px]">
              {businessLoading ? (
                <div className="flex items-center justify-center py-12 space-x-2 text-text-secondary">
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  <span className="text-sm">Loading businesses...</span>
                </div>
              ) : (
                <>
                  {/* Business searchable dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
                      Select Business <span className="text-error">*</span>
                      <span className="ml-1 normal-case font-normal text-text-secondary">(single select)</span>
                    </label>
                    <div className="relative" ref={dropdownRef}>
                      <div
                        className={`flex items-center border rounded-xl bg-background transition-all ${
                          dropdownOpen ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                        }`}
                      >
                        <Icon name="Search" size={15} className="ml-3 text-text-secondary shrink-0" />
                        <input
                          type="text"
                          value={bizSearch}
                          onChange={e => {
                            setBizSearch(e?.target?.value);
                            setDropdownOpen(true);
                            if (selectedBusiness && e?.target?.value !== selectedBusiness?.name) {
                              setSelectedBusiness(null);
                              setSelectedMerchantIds([]);
                            }
                          }}
                          onFocus={() => setDropdownOpen(true)}
                          placeholder="Search businesses..."
                          className="flex-1 px-3 py-2.5 text-sm bg-transparent text-text-primary placeholder-text-secondary focus:outline-none"
                        />
                        {selectedBusiness && (
                          <span className="mr-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium shrink-0">
                            Selected
                          </span>
                        )}
                        <button
                          onClick={() => setDropdownOpen(o => !o)}
                          className="mr-2 p-1 rounded text-text-secondary hover:text-text-primary transition-colors"
                          tabIndex={-1}
                        >
                          <Icon name={dropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={15} />
                        </button>
                      </div>

                      {dropdownOpen && (
                        <div className="absolute z-20 mt-1 w-full bg-surface border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {(() => {
                            const filtered = businesses?.filter(b =>
                              b?.name?.toLowerCase()?.includes(bizSearch?.toLowerCase())
                            );
                            if (filtered?.length === 0) {
                              return (
                                <div className="px-4 py-3 text-sm text-text-secondary text-center">
                                  No businesses found
                                </div>
                              );
                            }
                            return filtered?.map(biz => {
                              const isSelected = selectedBusiness?.id === biz?.id;
                              return (
                                <button
                                  key={biz?.id}
                                  onClick={() => handleSelectBusiness(biz)}
                                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors hover:bg-muted/30 ${
                                    isSelected ? 'bg-primary/5' : ''
                                  }`}
                                >
                                  <div
                                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                      isSelected ? 'border-primary bg-primary' : 'border-border'
                                    }`}
                                  >
                                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-text-primary'}`}>
                                      {biz?.name}
                                    </p>
                                    <p className="text-xs text-text-secondary">
                                      {biz?.merchants?.length} merchant{biz?.merchants?.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                  {isSelected && (
                                    <Icon name="Check" size={14} className="text-primary shrink-0" />
                                  )}
                                </button>
                              );
                            });
                          })()}
                        </div>
                      )}
                    </div>
                    {selectedBusiness && (
                      <p className="text-xs text-text-secondary mt-1.5">
                        <span className="font-medium text-primary">{selectedBusiness?.name}</span> selected
                        {' · '}
                        <button
                          onClick={() => { setSelectedBusiness(null); setSelectedMerchantIds([]); setBizSearch(''); }}
                          className="text-text-secondary hover:text-error underline"
                        >
                          Clear
                        </button>
                      </p>
                    )}
                  </div>

                  {/* Merchant selection */}
                  {selectedBusiness && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider">
                          Select Merchants <span className="text-error">*</span>
                          <span className="ml-1 normal-case font-normal text-text-secondary">(multi-select)</span>
                        </label>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={selectAllMerchants}
                            className="text-xs text-primary hover:underline"
                          >
                            Select all
                          </button>
                          <span className="text-text-secondary text-xs">·</span>
                          <button
                            onClick={clearAllMerchants}
                            className="text-xs text-text-secondary hover:text-text-primary hover:underline"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="border border-border rounded-xl overflow-hidden divide-y divide-border">
                        {selectedBusiness?.merchants?.map(merchant => {
                          const isChecked = selectedMerchantIds?.includes(merchant?.id);
                          return (
                            <label
                              key={merchant?.id}
                              className={`flex items-center space-x-3 px-4 py-3 cursor-pointer transition-colors ${
                                isChecked ? 'bg-primary/5' : 'bg-surface hover:bg-muted/20'
                              }`}
                            >
                              <div
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                  isChecked ? 'border-primary bg-primary' : 'border-border'
                                }`}
                              >
                                {isChecked && <Icon name="Check" size={10} className="text-white" />}
                              </div>
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={isChecked}
                                onChange={() => toggleMerchant(merchant?.id)}
                              />
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-medium ${isChecked ? 'text-primary' : 'text-text-primary'}`}>
                                  {merchant?.name}
                                </p>
                                <p className="text-xs text-text-secondary">{merchant?.id}</p>
                              </div>
                              {isChecked && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium shrink-0">
                                  Selected
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                      {selectedMerchantIds?.length > 0 && (
                        <p className="text-xs text-text-secondary mt-2">
                          <span className="font-semibold text-primary">{selectedMerchantIds?.length}</span> merchant{selectedMerchantIds?.length !== 1 ? 's' : ''} selected
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} className="text-error shrink-0" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Currency Selection ── */}
          {step === STEPS?.CURRENCY && (
            <div className="space-y-5 min-h-[280px]">
              <p className="text-sm text-text-secondary">
                Select one or more currencies to include in this settlement. A separate summary will be generated for each selected currency.
              </p>
              {currencyLoading ? (
                <div className="flex items-center justify-center py-12 space-x-2 text-text-secondary">
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  <span className="text-sm">Loading currencies...</span>
                </div>
              ) : currencies?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 space-y-2 text-text-secondary">
                  <Icon name="AlertCircle" size={24} className="text-warning" />
                  <p className="text-sm">No currencies available. Please try again.</p>
                  <button
                    onClick={fetchCurrencies}
                    className="text-xs text-primary hover:underline"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        Select Currencies <span className="text-error">*</span>
                        <span className="ml-1 normal-case font-normal text-text-secondary">(multi-select)</span>
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={selectAllCurrencies}
                          className="text-xs text-primary hover:underline"
                        >
                          Select all
                        </button>
                        <span className="text-text-secondary text-xs">·</span>
                        <button
                          onClick={clearAllCurrencies}
                          className="text-xs text-text-secondary hover:text-text-primary hover:underline"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="border border-border rounded-xl overflow-hidden divide-y divide-border">
                      {currencies?.map(curr => {
                        const isChecked = selectedCurrencies?.includes(curr?.currency_code);
                        return (
                          <label
                            key={curr?.currency_code}
                            className={`flex items-center space-x-3 px-4 py-3 cursor-pointer transition-colors ${
                              isChecked ? 'bg-primary/5' : 'bg-surface hover:bg-muted/20'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                isChecked ? 'border-primary bg-primary' : 'border-border'
                              }`}
                            >
                              {isChecked && <Icon name="Check" size={10} className="text-white" />}
                            </div>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={() => toggleCurrency(curr?.currency_code)}
                            />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium ${isChecked ? 'text-primary' : 'text-text-primary'}`}>
                                {curr?.currency_name}
                              </p>
                              <p className="text-xs text-text-secondary font-mono">{curr?.currency_code}</p>
                            </div>
                            {isChecked && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium shrink-0">
                                {curr?.currency_code}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    {selectedCurrencies?.length > 0 && (
                      <p className="text-xs text-text-secondary mt-2">
                        <span className="font-semibold text-primary">{selectedCurrencies?.length}</span> currenc{selectedCurrencies?.length !== 1 ? 'ies' : 'y'} selected:{' '}
                        {selectedCurrencies?.join(', ')}
                      </p>
                    )}
                  </div>
                </>
              )}
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} className="text-error shrink-0" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: Summary (per-currency) ── */}
          {step === STEPS?.SUMMARY && (
            <div className="space-y-6">
              {/* Period & selection info */}
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center space-x-1.5 bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-xs text-text-secondary">
                  <Icon name="Calendar" size={12} className="text-primary" />
                  <span>{startDate && endDate && `${format(new Date(startDate), 'MMM d, yyyy')} – ${format(new Date(endDate), 'MMM d, yyyy')}`}</span>
                </span>
                <span className="inline-flex items-center space-x-1.5 bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-xs text-text-secondary">
                  <Icon name="Building2" size={12} className="text-primary" />
                  <span>{selectedBusiness?.name}</span>
                </span>
                <span className="inline-flex items-center space-x-1.5 bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-xs text-text-secondary">
                  <Icon name="Users" size={12} className="text-primary" />
                  <span>{selectedMerchantIds?.length} merchant{selectedMerchantIds?.length !== 1 ? 's' : ''}</span>
                </span>
                <span className="inline-flex items-center space-x-1.5 bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-xs text-text-secondary">
                  <Icon name="DollarSign" size={12} className="text-primary" />
                  <span>{summaryDetails?.length} currenc{summaryDetails?.length !== 1 ? 'ies' : 'y'}</span>
                </span>
              </div>

              {/* Per-currency sections */}
              {summaryDetails?.map((detail, idx) => {
                const curr = detail?.currency || 'USD';
                const s = detail?.summary || {};
                const deposits = parseFloat(s?.total_deposits || 0);
                const payouts = parseFloat(s?.total_payouts || 0);
                const refunds = parseFloat(s?.total_refunds || 0);
                const mdr = parseFloat(s?.total_mdr || 0);
                const net = parseFloat(s?.net_settlement || 0);
                const merchantRows = detail?.merchant_settlements || [];

                // Check if there are no transactions (all summary values are null and no merchant rows)
                const hasNoTransactions =
                  s?.total_deposits === null &&
                  s?.total_payouts === null &&
                  s?.total_refunds === null &&
                  s?.total_mdr === null &&
                  s?.net_settlement === null &&
                  merchantRows?.length === 0;

                return (
                  <div key={`${curr}-${idx}`} className="border border-border rounded-xl overflow-hidden">
                    {/* Currency header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Coins" size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-text-primary">{curr}</p>
                          <p className="text-xs text-text-secondary">{detail?.business_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-text-secondary">Net Settlement</p>
                        <p className="text-sm font-bold text-primary">{hasNoTransactions ? '—' : formatCurrency(net, curr)}</p>
                      </div>
                    </div>

                    <div className="p-4 space-y-4">
                      {hasNoTransactions ? (
                        <div className="flex flex-col items-center justify-center py-8 space-y-3 text-center">
                          <div className="w-12 h-12 bg-muted/40 rounded-full flex items-center justify-center">
                            <Icon name="FileX" size={22} className="text-text-secondary" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-text-primary">No Transactions Found</p>
                            <p className="text-xs text-text-secondary mt-1">
                              There are no transactions for the selected period in {curr}.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Summary cards */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                              { label: 'Total Deposits', value: formatCurrency(deposits, curr), icon: 'TrendingUp', color: 'text-success' },
                              { label: 'Payouts', value: formatCurrency(payouts, curr), icon: 'ArrowUpRight', color: 'text-error' },
                              { label: 'Refunds', value: formatCurrency(refunds, curr), icon: 'RotateCcw', color: 'text-warning' },
                              { label: 'MDR', value: formatCurrency(mdr, curr), icon: 'Percent', color: 'text-text-secondary' },
                            ]?.map(card => (
                              <div key={card?.label} className="bg-muted/30 border border-border rounded-xl p-3">
                                <div className="flex items-center space-x-1.5 mb-1.5">
                                  <Icon name={card?.icon} size={13} className={card?.color} />
                                  <span className="text-xs text-text-secondary">{card?.label}</span>
                                </div>
                                <p className={`text-sm font-semibold ${card?.color}`}>{card?.value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Merchant breakdown table */}
                          {merchantRows?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">Merchant Breakdown</h4>
                              <div className="border border-border rounded-xl overflow-hidden">
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="bg-muted/30 border-b border-border">
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary">Merchant</th>
                                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-text-secondary">Deposits</th>
                                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-text-secondary">Payouts</th>
                                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-text-secondary">Refunds</th>
                                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-text-secondary">MDR</th>
                                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-text-secondary">Net</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                      {merchantRows?.map((m, i) => (
                                        <tr key={m?.merchant_id || i} className={i % 2 === 0 ? 'bg-surface' : 'bg-muted/10'}>
                                          <td className="px-4 py-3">
                                            <p className="font-medium text-text-primary text-xs">{m?.merchant_id || '—'}</p>
                                          </td>
                                          <td className="px-4 py-3 text-right text-xs text-success">{formatCurrency(parseFloat(m?.deposits || 0), curr)}</td>
                                          <td className="px-4 py-3 text-right text-xs text-error">{formatCurrency(parseFloat(m?.payouts || 0), curr)}</td>
                                          <td className="px-4 py-3 text-right text-xs text-warning">{formatCurrency(parseFloat(m?.refunds || 0), curr)}</td>
                                          <td className="px-4 py-3 text-right text-xs text-text-secondary">{formatCurrency(parseFloat(m?.mdr || 0), curr)}</td>
                                          <td className="px-4 py-3 text-right">
                                            <span className={`text-xs font-semibold ${parseFloat(m?.net || 0) >= 0 ? 'text-primary' : 'text-error'}`}>
                                              {formatCurrency(parseFloat(m?.net || 0), curr)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr className="bg-muted/40 border-t-2 border-border">
                                        <td className="px-4 py-3 text-xs font-bold text-text-primary">Total</td>
                                        <td className="px-4 py-3 text-right text-xs font-bold text-success">{formatCurrency(deposits, curr)}</td>
                                        <td className="px-4 py-3 text-right text-xs font-bold text-error">{formatCurrency(payouts, curr)}</td>
                                        <td className="px-4 py-3 text-right text-xs font-bold text-warning">{formatCurrency(refunds, curr)}</td>
                                        <td className="px-4 py-3 text-right text-xs font-bold text-text-secondary">{formatCurrency(mdr, curr)}</td>
                                        <td className="px-4 py-3 text-right text-xs font-bold text-primary">{formatCurrency(net, curr)}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} className="text-error shrink-0" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 5: Confirm ── */}
          {step === STEPS?.CONFIRM && (
            <div className="space-y-5">
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-4 flex items-start space-x-3">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-text-primary mb-1">Confirm Settlement Creation</p>
                  <p className="text-xs text-text-secondary">
                    You are about to create a settlement for <strong>{selectedBusiness?.name}</strong> covering{' '}
                    <strong>{selectedMerchantIds?.length} merchant{selectedMerchantIds?.length !== 1 ? 's' : ''}</strong> across{' '}
                    <strong>{summaryDetails?.length} currenc{summaryDetails?.length !== 1 ? 'ies' : 'y'}</strong>.
                  </p>
                </div>
              </div>

              {/* Settlement details */}
              <div className="bg-surface border border-border rounded-xl divide-y divide-border">
                {[
                  { label: 'Period', value: `${format(new Date(startDate), 'MMM d, yyyy')} – ${format(new Date(endDate), 'MMM d, yyyy')}` },
                  { label: 'Business', value: selectedBusiness?.name },
                  { label: 'Merchants', value: selectedMerchantIds?.length },
                  { label: 'Currencies', value: selectedCurrencies?.join(', ') },
                ]?.map(row => (
                  <div key={row?.label} className="flex items-center justify-between px-4 py-3">
                    <span className="text-xs text-text-secondary">{row?.label}</span>
                    <span className="text-xs font-medium text-text-primary">{row?.value}</span>
                  </div>
                ))}
                {summaryDetails?.map(detail => {
                  const curr = detail?.currency || 'USD';
                  const net = parseFloat(detail?.summary?.net_settlement || 0);
                  return (
                    <div key={curr} className="flex items-center justify-between px-4 py-3 bg-primary/5">
                      <span className="text-xs font-semibold text-text-primary">Net Settlement ({curr})</span>
                      <span className="text-xs font-bold text-primary">{formatCurrency(net, curr)}</span>
                    </div>
                  );
                })}
              </div>

              {/* Payment Summary per currency */}
              <div>
                <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Payment Summary</h4>
                <div className="space-y-4">
                  {summaryDetails?.map((detail, idx) => {
                    const curr = detail?.currency || 'USD';
                    const s = detail?.summary || {};
                    const deposits = parseFloat(s?.total_deposits || 0);
                    const payouts = parseFloat(s?.total_payouts || 0);
                    const refunds = parseFloat(s?.total_refunds || 0);
                    const mdr = parseFloat(s?.total_mdr || 0);
                    const net = parseFloat(s?.net_settlement || 0);
                    const fee = parseFloat(settlementFee) || 0;
                    const finalPayable = net - fee;

                    const hasNoTransactions =
                      s?.total_deposits === null &&
                      s?.total_payouts === null &&
                      s?.total_refunds === null &&
                      s?.total_mdr === null &&
                      s?.net_settlement === null;

                    return (
                      <div key={`confirm-${curr}-${idx}`} className="border border-border rounded-xl overflow-hidden">
                        {/* Currency header */}
                        <div className="flex items-center space-x-2 px-4 py-3 bg-muted/30 border-b border-border">
                          <div className="w-6 h-6 bg-primary/10 rounded-md flex items-center justify-center">
                            <Icon name="Coins" size={13} className="text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-text-primary">{curr}</span>
                          <span className="text-xs text-text-secondary">— {detail?.business_name}</span>
                        </div>

                        {hasNoTransactions ? (
                          <div className="px-4 py-5 flex items-center space-x-2 text-text-secondary">
                            <Icon name="FileX" size={15} className="shrink-0" />
                            <span className="text-xs">No transactions found for this currency.</span>
                          </div>
                        ) : (
                          <div className="p-4 space-y-3">
                            {/* Summary rows */}
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between py-1.5 border-b border-border/50">
                                <div className="flex items-center space-x-2">
                                  <Icon name="TrendingUp" size={13} className="text-success" />
                                  <span className="text-xs text-text-secondary">Total Deposits</span>
                                </div>
                                <span className="text-xs font-medium text-success">{formatCurrency(deposits, curr)}</span>
                              </div>
                              <div className="flex items-center justify-between py-1.5 border-b border-border/50">
                                <div className="flex items-center space-x-2">
                                  <Icon name="ArrowUpRight" size={13} className="text-error" />
                                  <span className="text-xs text-text-secondary">Total Payouts</span>
                                </div>
                                <span className="text-xs font-medium text-error">− {formatCurrency(payouts, curr)}</span>
                              </div>
                              <div className="flex items-center justify-between py-1.5 border-b border-border/50">
                                <div className="flex items-center space-x-2">
                                  <Icon name="RotateCcw" size={13} className="text-warning" />
                                  <span className="text-xs text-text-secondary">Total Refunds</span>
                                </div>
                                <span className="text-xs font-medium text-warning">− {formatCurrency(refunds, curr)}</span>
                              </div>
                              <div className="flex items-center justify-between py-1.5 border-b border-border/50">
                                <div className="flex items-center space-x-2">
                                  <Icon name="Percent" size={13} className="text-text-secondary" />
                                  <span className="text-xs text-text-secondary">MDR</span>
                                </div>
                                <span className="text-xs font-medium text-text-secondary">− {formatCurrency(mdr, curr)}</span>
                              </div>
                              <div className="flex items-center justify-between py-2 bg-primary/5 rounded-lg px-2 mt-1">
                                <div className="flex items-center space-x-2">
                                  <Icon name="Wallet" size={13} className="text-primary" />
                                  <span className="text-xs font-semibold text-text-primary">Net Settlement</span>
                                </div>
                                <span className="text-xs font-bold text-primary">{formatCurrency(net, curr)}</span>
                              </div>
                            </div>

                            {/* Settlement fee deduction */}
                            <div className="flex items-center justify-between py-1.5 border-t border-border/50 pt-2">
                              <div className="flex items-center space-x-2">
                                <Icon name="Minus" size={13} className="text-error" />
                                <span className="text-xs text-text-secondary">Settlement Fee</span>
                              </div>
                              <span className="text-xs font-medium text-error">− {formatCurrency(fee, curr)}</span>
                            </div>

                            {/* Final payable */}
                            <div className="flex items-center justify-between bg-success/5 border border-success/20 rounded-xl px-3 py-2.5">
                              <div className="flex items-center space-x-2">
                                <Icon name="CheckCircle" size={14} className="text-success" />
                                <span className="text-xs font-bold text-text-primary">Final Payable Amount</span>
                              </div>
                              <span className={`text-sm font-bold ${finalPayable >= 0 ? 'text-success' : 'text-error'}`}>
                                {formatCurrency(finalPayable, curr)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Settlement Fee Input */}
              <div className="bg-surface border border-border rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Icon name="Receipt" size={15} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-text-primary mb-0.5">
                      Settlement Fee
                    </label>
                    <p className="text-xs text-text-secondary mb-3">
                      This fee will be deducted from the net settlement amount for each currency.
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="relative flex-1 max-w-[200px]">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary font-medium">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={settlementFee}
                          onChange={e => setSettlementFee(e?.target?.value)}
                          className="w-full pl-7 pr-3 py-2.5 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                      <button
                        onClick={() => setSettlementFee(100)}
                        className="text-xs text-primary hover:underline"
                      >
                        Reset to default
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} className="text-error shrink-0" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── SUCCESS ── */}
          {step === STEPS?.SUCCESS && (
            <div className="flex flex-col items-center justify-center py-10 space-y-4 text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-text-primary">Settlement Created</h3>
                <p className="text-sm text-text-secondary mt-1">
                  Settlement successfully processed for{' '}
                  <span className="font-semibold">{selectedBusiness?.name}</span> — {selectedMerchantIds?.length} merchant{selectedMerchantIds?.length !== 1 ? 's' : ''},{' '}
                  {summaryDetails?.length} currenc{summaryDetails?.length !== 1 ? 'ies' : 'y'}.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {summaryDetails?.map(detail => (
                  <div key={detail?.currency} className="bg-muted/30 border border-border rounded-xl px-4 py-2 text-xs text-text-secondary">
                    <span className="font-semibold text-primary">{detail?.currency}</span>
                    {' · '}
                    Net: <span className="font-medium text-text-primary">{formatCurrency(parseFloat(detail?.summary?.net_settlement || 0), detail?.currency)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between shrink-0 bg-muted/10">
          <div>
            {(step === STEPS?.BUSINESS_MERCHANT || step === STEPS?.CURRENCY || step === STEPS?.SUMMARY || step === STEPS?.CONFIRM) && (
              <button
                onClick={goBack}
                className="flex items-center space-x-1.5 px-4 py-2 text-sm text-text-secondary hover:text-text-primary border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="ChevronLeft" size={14} />
                <span>Back</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {step === STEPS?.DATE_RANGE && (
              <button
                onClick={() => {
                  if (!startDate || !endDate) { setError('Please select both start and end dates.'); return; }
                  if (new Date(endDate) < new Date(startDate)) { setError('End date must be after start date.'); return; }
                  setError('');
                  setStep(STEPS?.BUSINESS_MERCHANT);
                }}
                disabled={!startDate || !endDate}
                className="flex items-center space-x-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={14} />
              </button>
            )}

            {step === STEPS?.BUSINESS_MERCHANT && (
              <button
                onClick={() => {
                  if (!selectedBusiness) { setError('Please select a business.'); return; }
                  if (selectedMerchantIds?.length === 0) { setError('Please select at least one merchant.'); return; }
                  setError('');
                  setStep(STEPS?.CURRENCY);
                }}
                disabled={!selectedBusiness || selectedMerchantIds?.length === 0}
                className="flex items-center space-x-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={14} />
              </button>
            )}

            {step === STEPS?.CURRENCY && (
              <button
                onClick={fetchSummary}
                disabled={summaryLoading || selectedCurrencies?.length === 0}
                className="flex items-center space-x-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {summaryLoading ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <>
                    <span>Fetch Summary</span>
                    <Icon name="ChevronRight" size={14} />
                  </>
                )}
              </button>
            )}

            {step === STEPS?.SUMMARY && (
              <button
                onClick={() => setStep(STEPS?.CONFIRM)}
                className="flex items-center space-x-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <span>Proceed to Confirm</span>
                <Icon name="ChevronRight" size={14} />
              </button>
            )}

            {step === STEPS?.CONFIRM && (
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="flex items-center space-x-2 px-5 py-2 bg-success text-white rounded-lg text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {confirming ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Icon name="CheckCircle" size={14} />
                    <span>Confirm Settlement</span>
                  </>
                )}
              </button>
            )}

            {step === STEPS?.SUCCESS && (
              <button
                onClick={onClose}
                className="px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSettlementModal;
